import { useCallback, useEffect, useState } from "react";
import { Blocker, useBlocker } from "react-router-dom";

interface useBeforeUnloadProps {
  isInitialTracking?: boolean;
}

/**
 * 사용자가 페이지를 벗어나려고 할 때 경고를 표시하는 Hook입니다.
 *
 * @param {useBeforeUnloadProps} params - 훅의 설정을 위한 매개변수 객체입니다.
 * @param {boolean} [params.isInitialTracking=true] - 초기에 unload 추적을 활성화할지 여부입니다. 기본값은 true입니다.
 * @returns {Object} 반환 객체는 다음 프로퍼티와 메서드를 포함합니다:
 *   @returns {boolean} isBlock - 현재 페이지를 나가려고 하는지 여부를 나타냅니다.
 *   @returns {Function} enableUnloadTracking - 현재 페이지를 나가려는 추적을 시작합니다.
 *   @returns {Function} disableUnloadTracking - 현재 페이지를 나가려는 추적을 중지합니다.
 *   @returns {Function} process - 차단 상태를 초기화하고 페이지 내비게이션을 진행할 수 있도록 합니다.
 *   @returns {Function} blocking - 페이지 이동을 계속 차단하여 사용자가 페이지를 벗어나는 것을 막습니다.
 *
 * @example
 * const {
 *   isBlock,
 *   enableUnloadTracking,
 *   disableUnloadTracking,
 *   process,
 *   blocking
 * } = useBeforeUnload({ isInitialTracking: true });
 *
 * enableUnloadTracking(); // unload 추적 활성화
 * disableUnloadTracking(); // unload 추적 비활성화
 */

const useBeforeUnload = ({
  isInitialTracking = true,
}: useBeforeUnloadProps = {}) => {
  const [isBlock, setIsBlock] = useState(false);
  const [isTracking, setIsTracking] = useState(isInitialTracking);
  const blocker: Blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isTracking && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked" && isTracking) {
      setIsBlock(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, blocker.state, isTracking]);

  const enableUnloadTracking = useCallback(() => {
    setIsTracking(true);
  }, []);

  const disableUnloadTracking = useCallback(() => {
    setIsTracking(false);
    setIsBlock(false);
    blocker?.proceed?.();
  }, []);

  const process = () => {
    isTracking && blocker?.proceed?.();
  };

  const blocking = () => {
    isTracking && blocker?.reset?.();
    setIsBlock(false);
  };

  return {
    isBlock,
    enableUnloadTracking,
    disableUnloadTracking,
    process,
    blocking,
  };
};

export default useBeforeUnload;
